import { IAppContext } from 'App/AppContext';
import IdListDTO from 'models/dto/IdListDTO';
import ProcessingActivity_TranslationDTO from 'models/dto/processingActivity/processingActivity_TranslationDTO';
import { ProcessingActivityContextDTO } from 'models/dto/processingActivity/processingActivityContextDTO';
import ProcessingActivityDTO from 'models/dto/processingActivity/processingActivityDTO';
import GlobalDataCache from 'models/globalDataCache/globalDataCache';
import ProcessingActivity from 'models/processingActivity';
import ProcessingActivity_Translation from 'models/processingActivity_Translation';
import { ProcessingActivityContext } from 'models/processingActivityContext';
import { fromApiDateTimeOptional, toApiDateTimeOptional } from 'utils/datetime';
import { mapFromAsset, mapToAsset } from './assetMapping';
import { mapFromControls } from './controlMappings';
import { mapFromLanguage } from './langMapping';
import { mapFromProcesses } from './processMapping';
import { mapFromRisksArray } from './riskMapping';
import { mapFromStringValue, mapToStringValue } from './stringValueMapping';
import { mapFromTask, mapToTask } from './taskMapping';

function mapToProcessingActivity_Translation(dto: ProcessingActivity_Translation): ProcessingActivity_TranslationDTO {
  const output = new ProcessingActivity_TranslationDTO();
  output.processingActivityId = dto.processingActivityId;
  output.languageId = dto.languageId;
  output.name = dto.name;
  output.description = dto.description;
  output.purpose = dto.purpose;
  output.lang = dto.lang;

  return output;
}

export function mapToProcessingActivity(processingActivity: ProcessingActivity): ProcessingActivityDTO {
  const output = new ProcessingActivityDTO();
  output.code = processingActivity.code;
  output.processingActivityId = processingActivity.processingActivityId;
  output.ownerId = processingActivity.ownerId;
  output.ownerRoleId = processingActivity.ownerRoleId;
  output.commentTrailId = processingActivity.commentTrailId;
  output.auditTrailId = processingActivity.auditTrailId;
  output.processingActivityStateId = processingActivity.state;
  output.processingActivityRoleId = processingActivity.role;
  output.trans = processingActivity.trans.map((t) => mapToProcessingActivity_Translation(t));
  output.authSchemaId = processingActivity.authSchemaId;
  output.tasks = processingActivity.tasks.map((t) => mapToTask(t));
  output.recipients = processingActivity.recipients.map((a) => mapToAsset(a));
  output.recipientCategories = processingActivity.recipientCategories.map((a) => mapToStringValue(a));
  output.processors = processingActivity.processors.map((a) => mapToAsset(a));
  output.data = processingActivity.data.map((a) => mapToAsset(a));
  output.jointControllers = processingActivity.joinControllers.map((a) => mapToAsset(a));
  output.responsibleEntitiesTenants = processingActivity.responsibleEntitiesTenants
    ? new IdListDTO(processingActivity.responsibleEntitiesTenants)
    : undefined;
  output.tagIds = processingActivity.tagIds
    ? new IdListDTO(processingActivity.tagIds.map((id) => id.toString()))
    : undefined;
  output.created = toApiDateTimeOptional(processingActivity.created);
  output.startDate = toApiDateTimeOptional(processingActivity.startDate);
  output.endDate = toApiDateTimeOptional(processingActivity.endDate);
  output.taskId_DPIA = processingActivity.taskId_DPIA;
  output.dataSubjects = processingActivity.dataSubjects.map((s) => mapToStringValue(s));
  output.dataCategories = processingActivity.dataCategories.map((s) => mapToStringValue(s));
  output.legalBases = processingActivity.legalBasis.map((s) => mapToStringValue(s));
  output.executingEntities = processingActivity.executingEntities.map((s) => mapToStringValue(s));
  output.retentionPeriod = processingActivity.retentionPeriod
    ? mapToStringValue(processingActivity.retentionPeriod)
    : undefined;
  output.dataSubjectScale = processingActivity.dataSubjectScale
    ? mapToStringValue(processingActivity.dataSubjectScale)
    : undefined;

  if (processingActivity.trans && processingActivity.transIdx >= 0) {
    output.trans[processingActivity.transIdx].name = processingActivity.name;
    output.trans[processingActivity.transIdx].description = processingActivity.description;
    output.trans[processingActivity.transIdx].purpose = processingActivity.purpose;
  }

  return output;
}

function mapFromProcessingActivity_Translation(dto: ProcessingActivity_TranslationDTO): ProcessingActivity_Translation {
  const output = new ProcessingActivity_Translation();
  output.processingActivityId = dto.processingActivityId;
  output.languageId = dto.languageId;
  output.name = dto.name;
  output.description = dto.description || '';
  output.purpose = dto.purpose || '';
  output.lang = mapFromLanguage(dto.lang);

  return output;
}

export function mapFromProcessingActivity(
  dto: ProcessingActivityDTO,
  globalDataCache: GlobalDataCache,
): ProcessingActivity {
  const output = new ProcessingActivity();
  output.processingActivityId = dto.processingActivityId;
  output.code = dto.code;
  output.ownerId = dto.ownerId;
  output.ownerRoleId = dto.ownerRoleId;
  output.commentTrailId = dto.commentTrailId;
  output.authSchemaId = dto.authSchemaId;
  output.auditTrailId = dto.auditTrailId;
  output.state = dto.processingActivityStateId;
  output.role = dto.processingActivityRoleId;
  output.trans = dto.trans ? dto.trans.map((t) => mapFromProcessingActivity_Translation(t)) : [];
  output.tasks = dto.tasks ? dto.tasks.map((t) => mapFromTask(t, globalDataCache)) : [];
  output.dpiaTask = dto.dpiaTask ? mapFromTask(dto.dpiaTask, globalDataCache) : undefined;
  output.recipients = dto.recipients ? dto.recipients.map((a) => mapFromAsset(a, globalDataCache)) : [];
  output.recipientCategories = dto.recipientCategories ? dto.recipientCategories.map((s) => mapFromStringValue(s)) : [];
  output.processors = dto.processors ? dto.processors.map((a) => mapFromAsset(a, globalDataCache)) : [];
  output.data = dto.data ? dto.data.map((a) => mapFromAsset(a, globalDataCache)) : [];
  output.joinControllers = dto.jointControllers
    ? dto.jointControllers.map((a) => mapFromAsset(a, globalDataCache))
    : [];
  output.responsibleEntitiesTenants = dto.responsibleEntitiesTenants?.idList ?? [];
  output.tagIds = dto.tagIds?.idList?.map((id: string) => Number(id));
  output.created = fromApiDateTimeOptional(dto.created);
  output.startDate = fromApiDateTimeOptional(dto.startDate);
  output.endDate = fromApiDateTimeOptional(dto.endDate);
  output.taskId_DPIA = dto.taskId_DPIA;
  output.dataSubjects = dto.dataSubjects ? dto.dataSubjects.map((s) => mapFromStringValue(s)) : [];
  output.dataCategories = dto.dataCategories ? dto.dataCategories.map((s) => mapFromStringValue(s)) : [];
  output.legalBasis = dto.legalBases ? dto.legalBases.map((s) => mapFromStringValue(s)) : [];
  output.executingEntities = dto.executingEntities ? dto.executingEntities.map((s) => mapFromStringValue(s)) : [];
  output.retentionPeriod = dto.retentionPeriod ? mapFromStringValue(dto.retentionPeriod) : undefined;
  output.dataSubjectScale = dto.dataSubjectScale ? mapFromStringValue(dto.dataSubjectScale) : undefined;
  output.approvalDate = fromApiDateTimeOptional(dto.approvalDate);

  if (output.trans && output.trans.length > 0) {
    output.transIdx = 0;
    output.name = output.trans[0].name;
    output.description = output.trans[0].description;
    output.purpose = output.trans[0].purpose;
  }

  return output;
}

export function mapFromProcessingActivities(
  processingActivityDtos: ProcessingActivityDTO[] | undefined,
  globalDataCache: GlobalDataCache,
): ProcessingActivity[] {
  if (!processingActivityDtos) return [];

  const output: ProcessingActivity[] = processingActivityDtos.map((dto) => {
    return mapFromProcessingActivity(dto, globalDataCache);
  });

  return output;
}

export function mapToProcessingActivities(processingActivities: ProcessingActivity[]): ProcessingActivityDTO[] {
  if (!processingActivities) return [];

  const output: ProcessingActivityDTO[] = processingActivities.map((pa) => {
    return mapToProcessingActivity(pa);
  });

  return output;
}

export function mapFromProcessingActivityContext(
  context: ProcessingActivityContextDTO,
  appContext: IAppContext,
): ProcessingActivityContext {
  const output = new ProcessingActivityContext();
  output.processes = mapFromProcesses(context.processes, appContext.globalDataCache);
  output.controls = mapFromControls(context.controls, appContext.globalDataCache);
  output.risks = mapFromRisksArray(context.risks, appContext);

  return output;
}
